import { DialogResult, OnSendNotification, GlobalInfrastructureServices } from './OutlookActions';
import { GeException } from './Helpers';
import { withTimeoutOr } from './utils';
import { NOTIFICATION_TIMEOUT_MS } from './constants';

/**
 * This class handles the presentation and data of interactive pop up dialogs
 */
export class NotificationDialog {
    protected globalService: GlobalInfrastructureServices;

    constructor(globalService: GlobalInfrastructureServices) {
        this.globalService = globalService;
    }

    /**
     * It saves the notification message to a common storage, so that the pop-up windows can access the data
     * @param messageId a unique identifier of the notification message
     * @param message the notification message
     */
    protected saveNotification(data: NotificationDialog.NotificationData, messageId: string) {
        localStorage.setItem(`msg_${messageId}`, JSON.stringify(data));
    }

    /**
     * It discards the stored space, so that these copies are not lingering after use
     * @param messageId the unique message id
     */
    protected discardNotification(messageId: string) {
        localStorage.removeItem(`msg_${messageId}`);
    }

    /**
     * Loads the recipients from the common storage
     * @param messageId the unique message  id
     */
    public loadNotification(messageId: string): NotificationDialog.NotificationData {
        const message = localStorage.getItem(`msg_${messageId}`);
        return message ? JSON.parse(message) : null;
    }

    /**
     * This function displays an inline notification message, above the send button.
     * @param messageType 
     * @param message 
     */
    protected async showInlineNotification(messageType: NotificationDialog.MessageType, message: string, interpolatedData?: { [key: string]: string },): Promise<string> {
        let type: Office.MailboxEnums.ItemNotificationMessageType;
        let icon: string = null;
        let fieldName = 'info';

        switch (messageType) {
            case NotificationDialog.MessageType.PROGRESS:
                type = Office.MailboxEnums.ItemNotificationMessageType.ProgressIndicator;
                fieldName = 'progress';
                break;

            case NotificationDialog.MessageType.INFO:
                type = Office.MailboxEnums.ItemNotificationMessageType.InformationalMessage;
                // icon = 'ge16_purple';
                break;

            default:
            case NotificationDialog.MessageType.ERROR:
                type = Office.MailboxEnums.ItemNotificationMessageType.ErrorMessage;
                break;
        }

        // Cosmetic banner update on the send-blocking path - bounded so an unresponsive host cannot
        // wedge the send over a notification (AB#14110).
        await withTimeoutOr(
            this.globalService.outlookActions.setNotificationMessage(fieldName, type, message ? this.globalService.t(message, interpolatedData) : null, icon),
            NOTIFICATION_TIMEOUT_MS, undefined, 'setNotificationMessage:dialog'
        );
        return '';
    }

    /**
    * This function triggers a new popup for the interactive portal to email association
    * @param emailPortals the recipient emails and all available portals for each email address
    */
    protected async showPopupNotification(
        messageType: NotificationDialog.MessageType,
        message: string,
        title: string,
        interpolatedData?: { [key: string]: string },
        buttons?: NotificationDialog.NotificationButton[]): Promise<string> {
        const dialogUuid = Math.random().toString(36).substring(2, 18) + Math.random().toString(36).substring(2, 18);

        return new Promise<string>(async (resolve, reject) => {

            const notificationData: NotificationDialog.NotificationData = {
                type: messageType,
                textId: message,
                titleId: title,
                buttons,
                interpolatedData
            };

            this.saveNotification(notificationData, dialogUuid);
            const dialogHandle = await this.globalService.outlookActions.showDialog(`https://${window.location.host}/notification?uuid=${dialogUuid}`, 50, 50, (result: DialogResult) => {

                //dialogHandle.close();
                //this.discardNotification(dialogUuid);

                if (!this.globalService.outlookActions.dialogEventHandler(result)) {
                    let dialogWasCancelled = true;
                    if (result && result.message) {

                        const reply = JSON.parse(result.message) as OnSendNotification;
                        if (reply.success) {
                            const replyMessage = reply.message

                            if (replyMessage) {
                                dialogWasCancelled = false;

                                // Allow a bit of delay here for the info dialog to go down and the progress dialog to appear
                                setTimeout(() => { resolve(replyMessage); }, 1000);
                            }
                            else {
                                reject(new GeException(null, 'Failed to get portal emails from localstorage'));
                            }
                        }
                    }

                    if (dialogWasCancelled) {
                        reject(new GeException('hook.cancelled', 'Email was cancelled'));
                    }
                }
                else {
                    reject(new GeException(null, 'Popup dialog was closed'));
                }

                dialogHandle.close();
                this.discardNotification(dialogUuid);
            });
        });
    }

    /**
     * This function displays a notification message.
     * 
     * @param messageFormat
     * @param messageType 
     * @param message 
     * @param buttons 
     */
    public async showNotificationMessage(
        messageFormat: NotificationDialog.NotificationFormat,
        messageType: NotificationDialog.MessageType,
        message: string,
        interpolatedData?: { [key: string]: string },
        title?: string,
        buttons?: NotificationDialog.NotificationButton[]
    ): Promise<string> {

        if (messageFormat == NotificationDialog.NotificationFormat.INLINE) {
            return this.showInlineNotification(messageType, message, interpolatedData);
        }
        else {
            return this.showPopupNotification(messageType, message, title, interpolatedData, buttons);
        }
    }
}

export namespace NotificationDialog {
    /**
     * This enum defines the type of the displayed notification message
     */
    export enum NotificationFormat {
        /** Display a new popup window. This is mostly used when interaction with the user is needed. */
        POPUP,

        /** 
         * Display the notification as an inline message, on top of the Send button.
         * This is mostly informative of the progress or a failure.
         */
        INLINE
    };

    /**
     * This enum defines the type of the displayed notification message
     */
    export enum MessageType {
        /** The notification is informative */
        INFO,

        /** The notification is about an error */
        ERROR,

        /** The notification is about a progress indicator */
        PROGRESS,

        /** The notification requires a boolean decision from the user. Only applicable with POPUPs */
        YES_NO
    };

    /**
     * This enum selects the rendering style of each button
     */
    export enum ButtonStyle {
        PRIMARY,
        DEFAULT,
        ERROR
    };

    /**
     * This interface defines the data of a notification button.
     */
    export interface NotificationButton {
        /** The unique id associated with the button */
        key: string,

        /** The translation id of the button text */
        textId: string,

        /** The rendering style of the button. If missing it defaults to DEFAULT */
        style?: ButtonStyle
    };

    /**
     * This interface defines the structure of the serialised data passed between a new dialog and the caller class.
     */
    export interface NotificationData {
        /** The unique id associated with the button */
        type: MessageType,

        /** The translation id of the notification title */
        titleId?: string,

        /** The translation id of the notification text */
        textId: string,

        /** The translation interpolated data */
        interpolatedData?: { [key: string]: string },

        /** An array of buttons */
        buttons: NotificationButton[],

        /** The key of the selected button */
        selectedButton?: string
    };
};
